
import React, { useState } from 'react';
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from '@/lib/utils';

interface ImageWithFallbackProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fallbackElement?: React.ReactNode;
  showSkeleton?: boolean;
  skeletonClassName?: string;
  priority?: "high" | "low" | "auto";
}

export const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({
  src,
  alt,
  className,
  fallbackElement,
  showSkeleton = true,
  skeletonClassName,
  priority = "auto",
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  // Determine if we should lazy load based on priority
  const shouldLazyLoad = priority === "low";
  
  return (
    <div className={cn("relative", className)}>
      {isLoading && showSkeleton && (
        <Skeleton
          className={cn(
            "absolute inset-0 z-10 rounded-md",
            skeletonClassName
          )}
        />
      )}
      
      {hasError ? (
        fallbackElement || <div className="flex items-center justify-center w-full h-full bg-background/20 rounded-md">{alt}</div>
      ) : (
        <img
          src={src}
          alt={alt}
          className={cn(
            "transition-opacity duration-300",
            isLoading ? "opacity-0" : "opacity-100",
            className
          )}
          onLoad={handleLoad}
          onError={handleError}
          loading={shouldLazyLoad ? "lazy" : "eager"}
          decoding={priority === "high" ? "sync" : "async"}
          {...props}
        />
      )}
    </div>
  );
};
